import React, { useEffect, useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import { useTranslation } from 'react-i18next';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { Nav, NavItem, TabContent, TabPane, UncontrolledTooltip } from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

import { GuidelineLayout } from './styles';
import Breadcrumbs from '../../components/Breadcrumb';
import Shimmer from 'components/Shimmer';
import PerformanceData from './components/Performance-Data';
import TitleGuideline from './components/Title-Guideline';
import ButtonBack from './components/Button-Back';
import ActionButton from './components/Action-Button';
import PostContentModal from './components/Post-Content-Modal';
import LinkButton from './components/Link-Button';
import SERPData from './components/SERP-Data';

const ContentApprovalBeta = () => {
  const firebaseHelper = getFirebaseBackend();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentAccountId = user?.account;
  const { t: translate } = useTranslation();
  const [content, setContent] = useState();
  const [modalApprove, setModalApprove] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [externalDocsLoading, setExternalDocsLoading] = useState(false);
  const [sendingToWordPress, setSendingToWordPress] = useState(false);
  const [activeNav, setActiveNav] = useState('1');

  const fetchData = async () => {
    if (!currentAccountId) return;
    setIsLoading(true);
    const loadedGuideline = await firebaseHelper.getFirebaseBetaGuideline(currentAccountId, id);

    // Links to old-module guidelines (e.g. from ClickUp) still point to /guideline/:id
    if (!loadedGuideline) {
      navigate(user?.adminStatus === 'Super Admin' ? `/old-guideline/${id}` : '/404', {
        replace: true,
      });
      return;
    }

    setContent(loadedGuideline);
    setIsLoading(false);
  };

  const toggleModalApprove = () => setModalApprove(!modalApprove);

  const handlePublishConfirm = () => {
    toggleModalApprove();
    fetchData();
  };

  async function handleGenerateExternalDocument() {
    setExternalDocsLoading(true);
    const firebaseToken = await firebaseHelper.getIdToken();

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/generateExternalDocs`,
        {
          id,
          accountId: currentAccountId,
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
      toast.success(translate('External document generated successfully'));
      const loadedGuideline = await firebaseHelper.getFirebaseBetaGuideline(currentAccountId, id);
      setContent(loadedGuideline);
    } catch (error) {
      toast.error(translate('Error generating external document'));
      console.log('Error generating external document:', error);
    } finally {
      setExternalDocsLoading(false);
    }
  }

  async function handleSendToWordPress() {
    setSendingToWordPress(true);
    const firebaseToken = await firebaseHelper.getIdToken();

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/postGuidelineDraft`,
        {
          id,
          accountId: currentAccountId,
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
      toast.success(translate('Sent to WordPress successfully'));
      const loadedGuideline = await firebaseHelper.getFirebaseBetaGuideline(currentAccountId, id);
      setContent(loadedGuideline);
    } catch (error) {
      toast.error(translate('Error sending to WordPress'));
      console.log('Error sending to WordPress:', error);
    } finally {
      setSendingToWordPress(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [currentAccountId]);

  return (
    <GuidelineLayout>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Content approval')} | Ectools`}</title>
        </MetaTags>
        <Breadcrumbs title={translate('Content')} breadcrumbItem={translate('Content approval')} />
        <div className="container-row container-general-information header-general-information">
          <div>
            <ButtonBack />
            {content?.primaryKeyword && <TitleGuideline guideline={content} />}
          </div>
          <div className="d-flex gap-2">
            <LinkButton
              icon="bx-file"
              label="Open Internal Document"
              href={`https://docs.google.com/document/d/${content?.googleDocId}`}
              disabled={!content?.googleDocId}
            />
            {content?.externalDocId ? (
              <LinkButton
                icon="bx-file"
                label="Open External Document"
                href={`https://docs.google.com/document/d/${content?.externalDocId}`}
              />
            ) : (
              <ActionButton
                icon="bx-file"
                label="Generate External Document"
                onClick={handleGenerateExternalDocument}
                disabled={!content?.googleDocId}
                loading={externalDocsLoading}
              />
            )}
            <ActionButton
              icon="bxl-wordpress"
              label="Send to WordPress"
              id="sendToWordPress"
              onClick={handleSendToWordPress}
              disabled={!content?.externalDocId || content?.status === 'PUBLISHED'}
              loading={sendingToWordPress}
            />
            {content?.status === 'PUBLISHED' && (
              <UncontrolledTooltip placement="top" target="sendToWordPress">
                {translate('Guideline was already published.')}
              </UncontrolledTooltip>
            )}
            {!content?.externalDocId && (
              <UncontrolledTooltip placement="top" target="sendToWordPress">
                {translate('To publish a draft the guideline needs an external document.')}
              </UncontrolledTooltip>
            )}
          </div>
        </div>
        <div className="container-row">
          <div className="container-general-information">
            <div className="header-general-information">
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={activeNav === '1' ? 'actived' : ''}
                    onClick={() => setActiveNav('1')}
                  >
                    {translate('Performance')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeNav === '2' ? 'actived' : ''}
                    onClick={() => setActiveNav('2')}
                  >
                    {translate('SERP data')}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
            <TabContent activeTab={activeNav}>
              {isLoading ? (
                <>
                  <Shimmer height="22em" style={{ marginBottom: '3em' }} />
                  <Shimmer height="10em" style={{ marginBottom: '3em' }} />
                  <Shimmer height="15em" />
                </>
              ) : (
                <>
                  <TabPane tabId="1">
                    <PerformanceData
                      primaryKeyword={content?.primaryKeyword}
                      secondaryKeywords={content?.secondaryKeywords}
                      status={content?.status}
                      publicationDate={content?.publishedDate}
                      publishedUrl={content?.publishedUrl}
                      onApprove={toggleModalApprove}
                    />
                  </TabPane>
                  <TabPane tabId="2">
                    <SERPData
                      data={content?.headers}
                      googleQuestionsData={content?.googleQuestions}
                      primaryKeyword={content?.primaryKeyword}
                    />
                  </TabPane>
                </>
              )}
            </TabContent>
          </div>
        </div>
        <PostContentModal
          isOpen={modalApprove}
          toggle={toggleModalApprove}
          accountId={currentAccountId}
          guidelineId={id}
          onConfirm={handlePublishConfirm}
        />
      </div>
    </GuidelineLayout>
  );
};

export default ContentApprovalBeta;
